import classNames from "classnames";
import { Carousel } from "components/Carousel";
import useLabels from "hooks/useLabels";
import { useEffect, useState } from "react";
import NumberInput from "./NumberInput";
import { Chip } from "design_system";

type PublishYearFilterProps = {
  onChange: (key: "yearMin" | "yearMax", value: number | undefined) => void;
  yearMin?: number | undefined;
  yearMax?: number | undefined;
  /**
   * If true, the first button will be focused when the component is mounted.
   */
  autoFocus?: boolean;
};

const currentYear = new Date().getFullYear();

export const PublishYearFilter = ({
  onChange,
  yearMin,
  yearMax,
}: PublishYearFilterProps) => {
  const [filterLabels] = useLabels("filter");
  const [pastYears, setPastYears] = useState<{
    2?: boolean;
    5?: boolean;
    10?: boolean;
  }>({ 2: false, 5: false, 10: false });

  useEffect(() => {
    if (yearMax === currentYear && yearMin) {
      const years = currentYear - yearMin;
      if ([2, 5, 10].includes(years)) {
        setPastYears({ [years]: true });
      } else {
        setPastYears({ 2: false, 5: false, 10: false });
      }
    } else {
      setPastYears({ 2: false, 5: false, 10: false });
    }
  }, [yearMin, yearMax]);

  const handleYearClick = (key: string) => () => {
    switch (key) {
      case filterLabels["all-years"]:
        onChange("yearMin", undefined);
        onChange("yearMax", undefined);
        setPastYears({ 2: false, 5: false, 10: false });
        break;
      case filterLabels["2-years"]:
        const minYear2 = currentYear - 2;
        onChange("yearMin", minYear2);
        onChange("yearMax", currentYear);
        setPastYears({ 2: true, 5: false, 10: false });
        break;
      case filterLabels["5-years"]:
        const minYear5 = currentYear - 5;
        onChange("yearMin", minYear5);
        onChange("yearMax", currentYear);
        setPastYears({ 2: false, 5: true, 10: false });
        break;
      case filterLabels["10-years"]:
        const minYear10 = currentYear - 10;
        onChange("yearMin", minYear10);
        onChange("yearMax", currentYear);
        setPastYears({ 2: false, 5: false, 10: true });
        break;
      default:
        break;
    }
  };

  return (
    // 288px is the width of the filter drawer with padding ensuring the carousel does not cause the drawer to overflow
    <div className="flex flex-col gap-2 sm:max-w-[288px]">
      <Carousel>
        <Chip
          isSelected={!yearMin && !yearMax}
          onClick={handleYearClick(filterLabels["all-years"])}
          size="sm"
        >
          {filterLabels["all-years"]}
        </Chip>
        <Chip
          isSelected={!!pastYears[2]}
          onClick={handleYearClick(filterLabels["2-years"])}
          size="sm"
        >
          {filterLabels["2-years"]}
        </Chip>
        <Chip
          isSelected={!!pastYears[5]}
          onClick={handleYearClick(filterLabels["5-years"])}
          size="sm"
        >
          {filterLabels["5-years"]}
        </Chip>
        <Chip
          isSelected={!!pastYears[10]}
          onClick={handleYearClick(filterLabels["10-years"])}
          size="sm"
        >
          {filterLabels["10-years"]}
        </Chip>
      </Carousel>

      <div className="flex gap-2 items-center">
        <NumberInput
          className="h-10 w-full"
          size="sm"
          placeholder="No min"
          min={1800}
          max={currentYear}
          value={yearMin || undefined}
          onChange={(value) => onChange("yearMin", value)}
        />
        <span className="text-fg-muted">&#8212;</span>
        <NumberInput
          className="h-10 w-full"
          size="sm"
          placeholder={`${currentYear}`}
          min={1800}
          max={currentYear}
          value={yearMax || currentYear}
          onChange={(value) => onChange("yearMax", value)}
        />
      </div>
    </div>
  );
};
